import { ed, svgText, ticks, niceMax, clip } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface RankedBarRow {
  id: string;
  label: string;
  value: number;
  /** Last period's value, drawn as a tick on the bar. */
  prior?: number | null;
  /** Spend the emphasis color on this row. */
  emphasis?: boolean;
  /** Draw this row in the caution color: it crossed a threshold that matters. */
  caution?: boolean;
}

interface RankedBarsProps {
  rows: RankedBarRow[];
  /** Draw a rule under this many rows with the note beside it. */
  cut?: { after: number; note: string };
  priorLabel?: string;
  format?: (v: number) => string;
  ariaLabel: string;
  /** Natural width; use ~420 in a side column so text stays legible. */
  width?: number;
}

const ROW = 19;

/** Sorted horizontal bars, gray by default, with prior-period ticks. */
export function RankedBars({
  rows,
  cut,
  priorLabel = "Last period",
  format = (v) => String(Math.round(v)),
  ariaLabel,
  width = 720,
}: RankedBarsProps) {
  const W = width;
  const M = { t: 22, r: 44, b: 22, l: width < 560 ? 120 : 150 };
  const { bind, node } = useTooltip();
  const max = niceMax(
    Math.max(1, ...rows.map((r) => Math.max(r.value, r.prior ?? 0))),
  );
  const iw = W - M.l - M.r;
  const x = (v: number) => M.l + (iw * v) / max;
  const H = M.t + rows.length * ROW + M.b;
  const firstWithPrior = rows.findIndex((r) => (r.prior ?? 0) > 0);

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {ticks(max).map((t) => (
          <g key={t}>
            <line
              x1={x(t)}
              x2={x(t)}
              y1={M.t - 4}
              y2={H - M.b}
              stroke={t === 0 ? ed.ink3 : ed.grid}
            />
            <text x={x(t)} y={H - 6} textAnchor="middle" style={svgText.axis}>
              {format(t)}
            </text>
          </g>
        ))}
        {rows.map((r, i) => {
          const y = M.t + i * ROW;
          const fill = r.caution ? ed.caution : r.emphasis ? ed.emph : ed.mark;
          const strong = r.emphasis || r.caution;
          return (
            <g
              key={r.id}
              {...bind(
                <>
                  <b>{r.label}</b>
                  <span className="num">{format(r.value)}</span> this period
                  {r.prior !== undefined && r.prior !== null && (
                    <>
                      , <span className="num">{format(r.prior)}</span>{" "}
                      {priorLabel.toLowerCase()}
                    </>
                  )}
                </>,
              )}
            >
              <rect x={0} y={y} width={W} height={ROW} fill="transparent" />
              <text
                x={M.l - 8}
                y={y + ROW / 2 + 4}
                textAnchor="end"
                style={strong ? svgText.strong : svgText.label}
              >
                {clip(r.label, 22)}
              </text>
              <rect
                x={M.l}
                y={y + 3}
                width={Math.max(0, x(r.value) - M.l)}
                height={ROW - 6}
                fill={fill}
              />
              {r.prior !== undefined && r.prior !== null && (
                <line
                  x1={x(r.prior)}
                  x2={x(r.prior)}
                  y1={y + 1}
                  y2={y + ROW - 1}
                  stroke={ed.ink}
                  strokeWidth={1.5}
                />
              )}
              <text
                x={x(r.value) + 5}
                y={y + ROW / 2 + 4}
                style={strong ? svgText.numStrong : svgText.num}
              >
                {format(r.value)}
              </text>
            </g>
          );
        })}
        {firstWithPrior >= 0 && (
          <text
            x={x(rows[firstWithPrior]!.prior ?? 0)}
            y={M.t - 8}
            textAnchor="middle"
            style={svgText.axis}
          >
            {priorLabel} ┆
          </text>
        )}
        {cut && cut.after > 0 && cut.after < rows.length && (
          <g>
            <line
              x1={M.l - 140}
              x2={W - 4}
              y1={M.t + cut.after * ROW}
              y2={M.t + cut.after * ROW}
              stroke={ed.ink}
              strokeDasharray="3 3"
            />
            <text
              x={W - 4}
              y={M.t + cut.after * ROW - 5}
              textAnchor="end"
              style={{ ...svgText.note, fontWeight: 600 }}
            >
              {cut.note}
            </text>
          </g>
        )}
      </svg>
      {node}
    </>
  );
}
